import { useEffect, useState } from "react";
import { v2StreamAnalysis, type AgentSSEEvent } from "../services/tradingAgentsV2";

interface AgentWorkflowViewProps {
  runId: string | null;
  statusLabel?: string;
}

const AGENT_ORDER = [
  "fundamentals_analyst",
  "sentiment_analyst",
  "news_analyst",
  "technical_analyst",
  "bull_researcher",
  "bear_researcher",
  "trader",
  "risk_management",
  "portfolio_manager",
];

const AGENT_LABELS: Record<string, string> = {
  system: "System",
  fundamentals_analyst: "Fundamentals",
  sentiment_analyst: "Sentiment",
  news_analyst: "News",
  technical_analyst: "Technical",
  bull_researcher: "Bull Researcher",
  bear_researcher: "Bear Researcher",
  trader: "Trader",
  risk_management: "Risk Manager",
  portfolio_manager: "Portfolio Mgr",
};

const STATUS_COLORS: Record<string, string> = {
  started: "bg-blue-100 text-blue-700 border-blue-300 animate-pulse",
  completed: "bg-green-100 text-green-700 border-green-300",
  error: "bg-red-100 text-red-700 border-red-300",
  pending: "bg-gray-100 text-gray-500 border-gray-200",
};

export function AgentWorkflowView({ runId, statusLabel }: AgentWorkflowViewProps) {
  const [events, setEvents] = useState<AgentSSEEvent[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!runId) {
      setEvents([]);
      setConnected(false);
      return;
    }
    setEvents([]);

    const es = v2StreamAnalysis(
      runId,
      (event) => {
        setConnected(true);
        setEvents((prev) => [...prev, event]);
      },
      () => setConnected(false)
    );

    return () => {
      es.close();
    };
  }, [runId]);

  if (!runId) {
    return (
      <p className="text-sm text-gray-500">
        Submit the form to start an analysis and see live agent updates.
      </p>
    );
  }

  const agentStatus: Record<string, AgentSSEEvent> = {};
  for (const ev of events) {
    if (ev.agent !== "system") {
      agentStatus[ev.agent] = ev;
    }
  }

  const systemEvents = events.filter((e) => e.agent === "system");
  const lastSystem = systemEvents[systemEvents.length - 1];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-gray-800">Run</span>
        <code className="rounded bg-gray-100 px-2 py-0.5 text-xs">{runId}</code>
        {statusLabel ? (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
            {statusLabel}
          </span>
        ) : null}
        <span
          className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
            connected
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-500"
          }`}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              connected ? "bg-green-500 animate-pulse" : "bg-gray-400"
            }`}
          />
          {connected ? "SSE Connected" : "Connecting..."}
        </span>
      </div>

      {lastSystem ? (
        <div
          className={`rounded-lg border px-3 py-2 text-sm ${
            lastSystem.event_type === "completed"
              ? "border-green-300 bg-green-50 text-green-800"
              : lastSystem.event_type === "error"
                ? "border-red-300 bg-red-50 text-red-800"
                : "border-blue-300 bg-blue-50 text-blue-800"
          }`}
        >
          {lastSystem.message}
        </div>
      ) : null}

      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        {AGENT_ORDER.map((agent) => {
          const ev = agentStatus[agent];
          const status = ev?.event_type || "pending";
          return (
            <div
              key={agent}
              className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-center transition-all ${STATUS_COLORS[status]}`}
              title={ev?.message || "Waiting..."}
            >
              <span className="text-[10px] font-semibold uppercase leading-tight">
                {AGENT_LABELS[agent] || agent}
              </span>
              {ev?.data?.summary ? (
                <span className="text-[9px] leading-tight opacity-80">
                  {(ev.data.summary as string).slice(0, 40)}
                  {(ev.data.summary as string).length > 40 ? "..." : ""}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {events.length > 0 ? (
        <details className="group">
          <summary className="cursor-pointer text-xs font-medium text-gray-500 hover:text-gray-700">
            Event log ({events.length} events)
          </summary>
          <div className="mt-2 max-h-48 overflow-y-auto rounded-lg bg-gray-900 p-3">
            {events.map((ev, i) => (
              <div key={i} className="text-[11px] leading-relaxed text-gray-300">
                <span className="text-gray-500">
                  {new Date(ev.timestamp * 1000).toLocaleTimeString()}
                </span>{" "}
                <span
                  className={
                    ev.event_type === "completed"
                      ? "text-green-400"
                      : ev.event_type === "error"
                        ? "text-red-400"
                        : ev.event_type === "started"
                          ? "text-blue-400"
                          : "text-yellow-400"
                  }
                >
                  [{ev.agent}]
                </span>{" "}
                <span className="text-gray-200">{ev.message}</span>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}
